import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Token storage
const TOKEN_KEY = 'tf_access_token';
const REFRESH_KEY = 'tf_refresh_token';
const tokenStore = typeof window !== 'undefined' ? window.sessionStorage : null;

function storeTokens(access, refresh) {
  if (!tokenStore) return;
  if (access) tokenStore.setItem(TOKEN_KEY, access);
  if (refresh) tokenStore.setItem(REFRESH_KEY, refresh);
}
function clearTokens() {
  if (!tokenStore) return;
  tokenStore.removeItem(TOKEN_KEY);
  tokenStore.removeItem(REFRESH_KEY);
}
function getAccessToken() {
  return tokenStore?.getItem(TOKEN_KEY) || null;
}
function getRefreshToken() {
  return tokenStore?.getItem(REFRESH_KEY) || null;
}

// Decode JWT payload without a library (base64url decode)
function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

// Build user profile from JWT token + any API response data
function buildUserFromToken(token, apiData) {
  const jwt = decodeJwtPayload(token);
  if (!jwt) return apiData || null;

  // JWT is the source of truth for role (can't be modified by proxies)
  const role = jwt.role || apiData?.role || 'worker';
  const isAdmin = role === 'admin';

  return {
    id: apiData?.id || jwt.sub,
    email: apiData?.email || jwt.email,
    name: apiData?.name || jwt.email,
    role: role,
    permissions: isAdmin
      ? ['dashboard','activities','vehicles','locations','turf_cuts','reports','calendar','costs','farm_map','store','team']
      : (apiData?.permissions || ['dashboard','activities','vehicles','locations','turf_cuts']),
    created_at: apiData?.created_at || '',
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = getAccessToken();
      if (!token) {
        setUser(false);
        setLoading(false);
        return;
      }
      const headers = { 'Authorization': `Bearer ${token}` };
      const { data } = await axios.get(`${API}/auth/me`, { headers });
      const profile = buildUserFromToken(token, data);
      console.log('[TF] checkAuth OK:', profile.role, profile.name);
      setUser(profile);
    } catch {
      console.log('[TF] checkAuth failed - clearing tokens');
      clearTokens();
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    console.log('[TF] Login API response role:', data.role);

    // Store tokens
    if (data.access_token) storeTokens(data.access_token, data.refresh_token);

    // Build profile from JWT (source of truth) + API data
    const token = data.access_token || getAccessToken();
    const profile = buildUserFromToken(token, data);
    console.log('[TF] Final user role:', profile.role, '| name:', profile.name, '| perms:', profile.permissions?.length);
    setUser(profile);
    return data;
  };

  const register = async (email, password, name, role = 'worker', permissions = null) => {
    const headers = {};
    const token = getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name, role, permissions }, { headers });
    return data;
  };

  const logout = async () => {
    try {
      const headers = {};
      const token = getAccessToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await axios.post(`${API}/auth/logout`, {}, { headers });
    } catch (err) { console.error('Logout failed:', err); }
    clearTokens();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export { getAccessToken, getRefreshToken, storeTokens, clearTokens };
